import { useState, useMemo } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter, Button } from '@repo/ui';
import { useEmailTemplates, getDefaultTemplateId, type EmailTemplate } from '../queries/useEmailTemplatesQuery';

/** Map of bill type to optional template override ID */
export type BulkTemplateOverrides = Record<string, string | undefined>;

interface EmailSendConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (emailTemplateOverrideId?: string, bulkOverrides?: BulkTemplateOverrides) => void;
  /** Title displayed in the header */
  title: string;
  /** Label for the confirm button */
  confirmLabel: string;
  /** Whether a mutation is currently pending */
  isPending: boolean;
  /** Whether recipient data is loading */
  isLoading: boolean;
  /** To recipients (after override logic applied) */
  toRecipients: string[];
  /** BCC recipients */
  bccRecipients: string[];
  /** Original recipients shown when override is active */
  actualRecipients: string[];
  /** Whether email override is active */
  overrideActive: boolean;
  /** Bill type for template pre-selection (single transaction mode) */
  billType?: 'operational' | 'evergreen' | 'trust';
  /** Whether this is a reminder/follow-up (vs initial send) */
  isReminder?: boolean;
  /** For bulk mode: number of transactions selected */
  bulkCount?: number;
  /**
   * For bulk mode: matter types present in the selection with their counts.
   * When multiple types are present, a separate template selector is shown per type.
   */
  bulkMatterTypes?: { type: 'operational' | 'evergreen' | 'trust'; count: number; isReminder: boolean }[];
  /** Callback to refresh contacts from Clio */
  onRefreshContacts?: () => void;
  /** Whether contacts refresh is in progress */
  isRefreshingContacts?: boolean;
}

export function EmailSendConfirmationModal({
  open,
  onClose,
  onConfirm,
  title,
  confirmLabel,
  isPending,
  isLoading,
  toRecipients,
  bccRecipients,
  actualRecipients,
  overrideActive,
  billType = 'operational',
  isReminder = false,
  bulkCount,
  bulkMatterTypes,
  onRefreshContacts,
  isRefreshingContacts,
}: EmailSendConfirmationModalProps) {
  const { data: templateData, isLoading: templatesLoading } = useEmailTemplates(open);

  // Single-type template selection state
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined);
  const [showAllTemplates, setShowAllTemplates] = useState(false);

  // Multi-type bulk template selection state (keyed by bill type)
  const [bulkSelectedTemplates, setBulkSelectedTemplates] = useState<Record<string, string | undefined>>({});
  const [bulkShowAllTemplates, setBulkShowAllTemplates] = useState<Record<string, boolean>>({});

  const isBulk = bulkCount != null && bulkCount > 0;
  const hasMultipleTypes = isBulk && bulkMatterTypes && bulkMatterTypes.length > 1;

  // Single-type defaults
  const defaultTemplateId = useMemo(() => {
    if (!templateData?.configured) return undefined;
    return getDefaultTemplateId(templateData.configured, billType, isReminder);
  }, [templateData?.configured, billType, isReminder]);

  const effectiveTemplateId = selectedTemplateId ?? defaultTemplateId;

  const defaultTemplate = useMemo(() => {
    if (!defaultTemplateId || !templateData?.allTemplates) return undefined;
    return templateData.allTemplates.find(t => t.templateId === defaultTemplateId);
  }, [defaultTemplateId, templateData?.allTemplates]);

  const otherTemplates = useMemo(() => {
    if (!templateData?.allTemplates) return [];
    return templateData.allTemplates.filter(t => t.templateId !== defaultTemplateId);
  }, [templateData?.allTemplates, defaultTemplateId]);

  const handleConfirm = () => {
    if (hasMultipleTypes && bulkMatterTypes) {
      // Build per-type override map
      const overrides: BulkTemplateOverrides = {};
      for (const { type, isReminder: typeIsReminder } of bulkMatterTypes) {
        const typeDefault = templateData?.configured
          ? getDefaultTemplateId(templateData.configured, type, typeIsReminder)
          : undefined;
        const selected = bulkSelectedTemplates[type];
        overrides[type] = selected && selected !== typeDefault ? selected : undefined;
      }
      onConfirm(undefined, overrides);
    } else {
      const overrideId = selectedTemplateId && selectedTemplateId !== defaultTemplateId
        ? selectedTemplateId
        : undefined;
      onConfirm(overrideId);
    }
  };

  const handleClose = () => {
    setSelectedTemplateId(undefined);
    setShowAllTemplates(false);
    setBulkSelectedTemplates({});
    setBulkShowAllTemplates({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} zIndex="z-[300]" size="xl">
      <DialogHeader>{title}</DialogHeader>
      <DialogBody>
        <div className="space-y-4">
          {/* Template Selection Section */}
          <div>
            {templatesLoading ? (
              <div className="text-sm text-xtnd-dark-400">Loading templates...</div>
            ) : hasMultipleTypes && bulkMatterTypes ? (
              /* Multi-type bulk: one selector per matter type */
              <div className="space-y-4">
                {bulkMatterTypes.map(({ type, count, isReminder: typeIsReminder }) => (
                  <BulkTypeTemplateSelector
                    key={type}
                    billType={type}
                    count={count}
                    isReminder={typeIsReminder}
                    templateData={templateData!}
                    selectedTemplateId={bulkSelectedTemplates[type]}
                    onSelectTemplate={(id) => setBulkSelectedTemplates(prev => ({ ...prev, [type]: id }))}
                    showAll={bulkShowAllTemplates[type] || false}
                    onToggleShowAll={() => setBulkShowAllTemplates(prev => ({ ...prev, [type]: !prev[type] }))}
                  />
                ))}
              </div>
            ) : (
              /* Single-type: original selector */
              <>
                <div className="text-sm font-semibold text-xtnd-dark-700 dark:text-xtnd-dark-200 mb-2">Email Template</div>
                <div className="space-y-2">
                  {defaultTemplate ? (
                    <TemplateRadio
                      template={defaultTemplate}
                      isDefault
                      checked={effectiveTemplateId === defaultTemplateId}
                      radioName="email-template"
                      onChange={() => setSelectedTemplateId(undefined)}
                    />
                  ) : defaultTemplateId ? (
                    <TemplateRadioFallback templateId={defaultTemplateId} />
                  ) : (
                    <div className="text-sm text-xtnd-dark-400 italic">No default template configured for this type</div>
                  )}

                  {otherTemplates.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowAllTemplates(!showAllTemplates)}
                      className="text-sm text-xtnd-blue hover:text-xtnd-blue-dark dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                    >
                      {showAllTemplates
                        ? 'Show less'
                        : `Show ${otherTemplates.length} more template${otherTemplates.length === 1 ? '' : 's'}`}
                    </button>
                  )}

                  {showAllTemplates && otherTemplates.map(template => (
                    <TemplateRadio
                      key={template.templateId}
                      template={template}
                      checked={effectiveTemplateId === template.templateId}
                      radioName="email-template"
                      onChange={() => setSelectedTemplateId(template.templateId)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Divider */}
          <hr className="border-xtnd-dark-200 dark:border-xtnd-dark-600" />

          {/* Recipients Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-xtnd-dark-700 dark:text-xtnd-dark-200">Recipients</div>
              {onRefreshContacts && !isBulk && (
                <button
                  type="button"
                  onClick={onRefreshContacts}
                  disabled={isRefreshingContacts}
                  className="text-xs text-xtnd-blue hover:text-xtnd-blue-dark dark:text-blue-400 dark:hover:text-blue-300 font-medium disabled:opacity-50"
                >
                  {isRefreshingContacts ? 'Refreshing...' : 'Refresh from Clio'}
                </button>
              )}
            </div>

            {isBulk ? (
              <div className="text-sm text-xtnd-dark-600">
                {bulkCount} transaction{bulkCount === 1 ? '' : 's'} selected
                {bulkMatterTypes && bulkMatterTypes.length > 1 && (
                  <span className="text-xtnd-dark-400 ml-1">
                    ({bulkMatterTypes.map(t => `${t.count} ${typeLabel(t.type)}`).join(', ')})
                  </span>
                )}
              </div>
            ) : isLoading ? (
              <div className="text-sm text-xtnd-dark-400">Loading recipients...</div>
            ) : (
              <div className="space-y-3">
                <div>
                  <div className="text-xs font-medium text-xtnd-dark-500 mb-1">
                    {overrideActive ? 'Email will be sent to (Override):' : 'To:'}
                  </div>
                  {toRecipients.length > 0 ? (
                    <ul className="list-disc list-inside text-sm">
                      {toRecipients.map(e => (
                        <li key={e} className={overrideActive ? 'font-semibold text-xtnd-blue' : ''}>
                          {e}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-sm text-xtnd-dark-400">No recipient found</div>
                  )}
                </div>

                {overrideActive && actualRecipients.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-xtnd-dark-500 mb-1">
                      Original Client Recipients (for reference):
                    </div>
                    <ul className="list-disc list-inside text-sm text-xtnd-dark-500">
                      {actualRecipients.map(e => (
                        <li key={e} className="line-through">{e}</li>
                      ))}
                    </ul>
                    <div className="text-xs text-xtnd-dark-400 mt-1">
                      These recipients will NOT receive the email due to override setting
                    </div>
                  </div>
                )}

                {bccRecipients.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-xtnd-dark-500 mb-1">BCC:</div>
                    <ul className="list-disc list-inside text-sm">
                      {bccRecipients.map(e => (
                        <li key={e}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={isPending || isLoading || (!isBulk && toRecipients.length === 0)}
        >
          {isPending ? 'Sending...' : confirmLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ── Sub-components ────────────────────────────────────────

function typeLabel(type: 'operational' | 'evergreen' | 'trust'): string {
  if (type === 'evergreen') return 'Evergreen';
  if (type === 'trust') return 'Trust';
  return 'Operational';
}

/** Template selector for a single bill type within a multi-type bulk modal */
function BulkTypeTemplateSelector({
  billType,
  count,
  isReminder,
  templateData,
  selectedTemplateId,
  onSelectTemplate,
  showAll,
  onToggleShowAll,
}: {
  billType: 'operational' | 'evergreen' | 'trust';
  count: number;
  isReminder: boolean;
  templateData: { configured: any; allTemplates: EmailTemplate[] };
  selectedTemplateId: string | undefined;
  onSelectTemplate: (id: string | undefined) => void;
  showAll: boolean;
  onToggleShowAll: () => void;
}) {
  const defaultId = getDefaultTemplateId(templateData.configured, billType, isReminder);
  const effectiveId = selectedTemplateId ?? defaultId;
  const defaultTpl = defaultId
    ? templateData.allTemplates.find(t => t.templateId === defaultId)
    : undefined;
  const others = templateData.allTemplates.filter(t => t.templateId !== defaultId);
  const radioName = `email-template-${billType}`;

  return (
    <div className="border border-xtnd-dark-200 dark:border-xtnd-dark-600 rounded-lg p-3">
      <div className="text-sm font-semibold text-xtnd-dark-700 dark:text-xtnd-dark-200 mb-2">
        {typeLabel(billType)} Template
        <span className="ml-2 text-xs font-normal text-xtnd-dark-400">({count} transaction{count === 1 ? '' : 's'})</span>
      </div>
      <div className="space-y-2">
        {defaultTpl ? (
          <TemplateRadio
            template={defaultTpl}
            isDefault
            checked={effectiveId === defaultId}
            radioName={radioName}
            onChange={() => onSelectTemplate(undefined)}
          />
        ) : defaultId ? (
          <TemplateRadioFallback templateId={defaultId} />
        ) : (
          <div className="text-sm text-xtnd-dark-400 italic">No default template configured</div>
        )}

        {others.length > 0 && (
          <button
            type="button"
            onClick={onToggleShowAll}
            className="text-sm text-xtnd-blue hover:text-xtnd-blue-dark dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            {showAll
              ? 'Show less'
              : `Show ${others.length} more template${others.length === 1 ? '' : 's'}`}
          </button>
        )}

        {showAll && others.map(template => (
          <TemplateRadio
            key={template.templateId}
            template={template}
            checked={effectiveId === template.templateId}
            radioName={radioName}
            onChange={() => onSelectTemplate(template.templateId)}
          />
        ))}
      </div>
    </div>
  );
}

/** Reusable radio row for a template */
function TemplateRadio({
  template,
  isDefault,
  checked,
  radioName,
  onChange,
}: {
  template: EmailTemplate;
  isDefault?: boolean;
  checked: boolean;
  radioName: string;
  onChange: () => void;
}) {
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
        checked
          ? 'border-xtnd-blue bg-blue-50 dark:bg-blue-900/30'
          : 'border-xtnd-dark-200 hover:border-xtnd-dark-300 dark:border-xtnd-dark-600 dark:hover:border-xtnd-dark-500'
      }`}
    >
      <input
        type="radio"
        name={radioName}
        checked={checked}
        onChange={onChange}
        className="mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-100">
          {template.templateName}
          {isDefault && (
            <span className="ml-2 text-xs font-normal text-xtnd-blue">(Default)</span>
          )}
        </div>
        {template.description && (
          <div className="text-xs text-xtnd-dark-500 mt-0.5">{template.description}</div>
        )}
        <TemplateMeta template={template} />
      </div>
    </label>
  );
}

/** Fallback radio for when template metadata isn't loaded but we have the ID */
function TemplateRadioFallback({ templateId }: { templateId: string }) {
  return (
    <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer border-xtnd-blue bg-blue-50 dark:bg-blue-900/30">
      <input type="radio" name="email-template" checked readOnly className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-100">
          {templateId}
          <span className="ml-2 text-xs font-normal text-xtnd-blue dark:text-blue-400">(Default)</span>
        </div>
      </div>
    </label>
  );
}

/** Small metadata row showing created date and tags for a template */
function TemplateMeta({ template }: { template: EmailTemplate }) {
  const tags = Array.isArray(template.tags) ? template.tags : [];
  const createdDate = template.createdAt
    ? new Date(template.createdAt).toLocaleDateString()
    : undefined;

  if (!createdDate && tags.length === 0) return null;

  return (
    <div className="flex items-center gap-2 mt-1 flex-wrap">
      {createdDate && (
        <span className="text-xs text-xtnd-dark-400">Created {createdDate}</span>
      )}
      {tags.map(tag => (
        <span
          key={tag}
          className="inline-block text-xs px-1.5 py-0.5 bg-xtnd-dark-100 text-xtnd-dark-600 dark:bg-xtnd-dark-700 dark:text-xtnd-dark-300 rounded"
        >
          {tag}
        </span>
      ))}
    </div>
  );
}
